import React from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Combobox } from '../../ui/combobox';

const OrderDetailsTab = ({ orderData, setOrderData, customers, selectedCustomer, setSelectedCustomer, selectedCustomerData, selectedMachine, setSelectedMachine, getConsigneeOptions }) => {
  return (
    <>
      <div className="space-y-3">
        <h3 className="font-bold text-sm text-slate-900 bg-[#a0a0a0] -mx-6 px-6 py-2">Customer Information</h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Select Customer *</Label>
            <div className="mt-1">
              <Combobox
                options={customers.map(c => ({
                  value: c.id,
                  label: `${c.customer_name || c.addresses?.[0]?.customer_name_child || c.parent_customer_name}${c.credibility_rating ? ` (${c.credibility_rating})` : ''}`
                }))}
                value={selectedCustomer}
                onValueChange={setSelectedCustomer}
                placeholder="Search and select customer..."
                searchPlaceholder="Search customers..."
                className="h-9 text-xs"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Biller</Label>
            <div className="mt-1">
              {selectedCustomerData?.billers?.filter(b => b.biller_name).length > 0 ? (
                <Combobox
                  options={selectedCustomerData.billers.filter(b => b.biller_name).map(b => ({ value: b.biller_name, label: `${b.biller_name}${b.address ? ` - ${b.address}` : ''}` }))}
                  value={orderData.biller}
                  onValueChange={(v) => setOrderData({ ...orderData, biller: v })}
                  placeholder="Select biller..."
                  searchPlaceholder="Search..."
                  className="h-9 text-xs"
                />
              ) : (
                <Input className="h-9 text-xs" value={orderData.biller} onChange={(e) => setOrderData({ ...orderData, biller: e.target.value })} placeholder="Enter biller name..." />
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs">Consignee</Label>
            <div className="mt-1">
              {getConsigneeOptions().length > 0 ? (
                <Combobox options={getConsigneeOptions()} value={orderData.consignee} onValueChange={(v) => setOrderData({ ...orderData, consignee: v })} placeholder="Select consignee..." searchPlaceholder="Search consignees..." className="h-9 text-xs" />
              ) : (
                <Input className="h-9 text-xs" value={orderData.consignee} onChange={(e) => setOrderData({ ...orderData, consignee: e.target.value })} placeholder="Enter consignee name..." />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="font-bold text-sm text-slate-900 bg-[#a0a0a0] -mx-6 px-6 py-2">Manufacturing & Branding</h3>
        <div className="grid grid-cols-4 gap-3">
          {[
            { key: 'manufactured_by', label: 'Manufactured By' },
            { key: 'marketed_by', label: 'Marketed By' },
            { key: 'brand_name', label: 'Brand Name' },
            { key: 'product_name', label: 'Product Name' },
            { key: 'qty', label: 'Quantity (g/lt/Nos)' },
          ].map(({ key, label }) => (
            <div key={key}>
              <Label className="text-xs">{label}</Label>
              <Input className="mt-1 h-9 text-xs bg-white" value={orderData[key]} onChange={(e) => setOrderData({ ...orderData, [key]: e.target.value })} />
            </div>
          ))}
          <div>
            <Label className="text-xs">Packed Date</Label>
            <Input type="date" className="mt-1 h-9 text-xs bg-white" value={orderData.packed_date} onChange={(e) => setOrderData({ ...orderData, packed_date: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Bar Code Number</Label>
            <Input className="mt-1 h-9 text-xs bg-white" value={orderData.bar_code} onChange={(e) => setOrderData({ ...orderData, bar_code: e.target.value })} />
          </div>
        </div>
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Label className="text-xs text-blue-900 font-semibold">Auto-Generated Job Name *</Label>
          <p className="text-sm text-blue-900 mt-1 font-bold break-words whitespace-pre-wrap">
            {orderData.job_name || 'Fill in the fields above to generate job name'}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="font-bold text-sm text-slate-900 bg-[#a0a0a0] -mx-6 px-6 py-2">Approval & Delivery Dates</h3>
        <div className="grid grid-cols-4 gap-3">
          <div><Label className="text-xs">Design Approved Date</Label><Input type="date" className="mt-1 h-9 text-xs bg-white" value={orderData.design_approved_date} onChange={(e) => setOrderData({ ...orderData, design_approved_date: e.target.value })} /></div>
          <div><Label className="text-xs">Separations Approved Date</Label><Input type="date" className="mt-1 h-9 text-xs bg-white" value={orderData.separations_approved_date} onChange={(e) => setOrderData({ ...orderData, separations_approved_date: e.target.value })} /></div>
          <div><Label className="text-xs">Delivery Date</Label><Input type="date" className="mt-1 h-9 text-xs bg-white" value={orderData.delivery_date} onChange={(e) => setOrderData({ ...orderData, delivery_date: e.target.value })} /></div>
          <div><Label className="text-xs">Pricing</Label><Input type="number" step="0.01" className="mt-1 h-9 text-xs bg-white" value={orderData.pricing} onChange={(e) => setOrderData({ ...orderData, pricing: e.target.value })} /></div>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="font-bold text-sm text-slate-900 bg-[#a0a0a0] -mx-6 px-6 py-2">Cylinder Specifications</h3>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">Height (mm) *</Label><Input type="number" step="0.01" className="mt-1 h-9 text-xs bg-white" value={orderData.height} onChange={(e) => setOrderData({ ...orderData, height: e.target.value })} required /></div>
          <div><Label className="text-xs">Ups in Circum *</Label><Input type="number" step="1" className="mt-1 h-9 text-xs bg-white" value={orderData.ups_in_circum} onChange={(e) => setOrderData({ ...orderData, ups_in_circum: e.target.value })} required /></div>
          <div><Label className="text-xs">Circumference (mm) - Calculated</Label><Input type="number" step="0.01" className="mt-1 h-9 text-xs bg-slate-100" value={orderData.circumference} readOnly disabled /></div>
        </div>
        <div className="grid grid-cols-6 gap-3">
          <div><Label className="text-xs">Width (mm) *</Label><Input type="number" step="0.01" className="mt-1 h-9 text-xs bg-white" value={orderData.width} onChange={(e) => setOrderData({ ...orderData, width: e.target.value })} required /></div>
          <div><Label className="text-xs">Ups in Length *</Label><Input type="number" step="1" className="mt-1 h-9 text-xs bg-white" value={orderData.ups_in_length} onChange={(e) => setOrderData({ ...orderData, ups_in_length: e.target.value })} required /></div>
          <div><Label className="text-xs">Trimming (mm)</Label><Input type="number" step="0.01" className="mt-1 h-9 text-xs bg-white" value={orderData.trimming} onChange={(e) => setOrderData({ ...orderData, trimming: e.target.value })} /></div>
          <div><Label className="text-xs">Extra Length (mm)</Label><Input type="number" step="0.01" className="mt-1 h-9 text-xs bg-white" value={orderData.extra_length} onChange={(e) => setOrderData({ ...orderData, extra_length: e.target.value })} /></div>
          <div><Label className="text-xs">Actual Extra Length (20-200mm)</Label><Input type="number" step="0.01" min="20" max="200" className="mt-1 h-9 text-xs bg-white" value={orderData.actual_extra_length} onChange={(e) => setOrderData({ ...orderData, actual_extra_length: e.target.value })} /></div>
          <div><Label className="text-xs">Length (mm) - Calculated</Label><Input type="number" step="0.01" className="mt-1 h-9 text-xs bg-slate-100" value={orderData.length} readOnly disabled /></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">New Baseshell</Label><Input type="number" className="mt-1 h-9 text-xs bg-white" value={orderData.new_baseshell} onChange={(e) => setOrderData({ ...orderData, new_baseshell: e.target.value })} placeholder="0" /></div>
          <div><Label className="text-xs">PBS</Label><Input type="number" className="mt-1 h-9 text-xs bg-white" value={orderData.pbs} onChange={(e) => setOrderData({ ...orderData, pbs: e.target.value })} placeholder="0" /></div>
          <div><Label className="text-xs">Number of Cylinders *</Label><Input type="number" className="mt-1 h-9 text-xs bg-slate-100" value={orderData.nos_of_cylinders} readOnly disabled /></div>
        </div>
        <div className="pt-1">
          <h4 className="font-medium text-xs text-slate-700 mb-2">Machine & Bore Details</h4>
          {selectedCustomerData?.bore_details && selectedCustomerData.bore_details.filter(m => m.machine_name).length > 0 && (
            <div className="mb-3">
              <Label className="text-xs">Select Machine (Auto-fills Bore & Degree)</Label>
              <div className="mt-1">
                <Combobox
                  options={selectedCustomerData.bore_details.filter(m => m.machine_name).map((machine) => ({
                    value: machine.machine_name,
                    label: `${machine.machine_name} (OD: ${machine.outer_diameter}mm, Deg: ${machine.degree}\u00B0${machine.key_way ? `, KW: ${machine.key_way}` : ''})`
                  }))}
                  value={selectedMachine}
                  onValueChange={setSelectedMachine}
                  placeholder="Search and select machine..."
                  searchPlaceholder="Search machines..."
                  className="h-9 text-xs"
                />
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Bore / Outer Diameter (mm) *</Label><Input type="number" step="0.01" className="mt-1 h-9 text-xs bg-white" value={orderData.bore} onChange={(e) => setOrderData({ ...orderData, bore: e.target.value })} /></div>
            <div><Label className="text-xs">Degree *</Label><Input type="number" step="0.01" className="mt-1 h-9 text-xs bg-white" value={orderData.degree} onChange={(e) => setOrderData({ ...orderData, degree: e.target.value })} /></div>
            <div><Label className="text-xs">Key Way</Label><Input className="mt-1 h-9 text-xs bg-white" value={orderData.key_way} onChange={(e) => setOrderData({ ...orderData, key_way: e.target.value })} placeholder="Key way..." /></div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderDetailsTab;
